import { useState, useEffect } from 'react';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { useUser as useDevUser } from '../contexts/DevAuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  User,
  Bell,
  CreditCard,
  Check,
  ExternalLink,
  Palette,
  Plug,
} from 'lucide-react';
import { fetchBilling, createCheckout, createPortalSession } from '../api/billing';
import ThemeToggle from '../components/ui/ThemeToggle';
import AccentColorPicker from '../components/ui/AccentColorPicker';

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const hasValidClerkKey = clerkPubKey && clerkPubKey.startsWith('pk_') && clerkPubKey.length > 30;
const useUser = hasValidClerkKey ? useClerkUser : useDevUser;

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '/mo',
    features: ['25 backlinks', '72h check interval', '1 project', 'Email alerts'],
    current: true,
  },
  {
    id: 'starter',
    name: 'Starter',
    price: '$19',
    period: '/mo',
    features: ['250 backlinks', '48h check interval', '3 projects', 'Screenshots', 'Slack alerts'],
    priceId: 'price_starter',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$49',
    period: '/mo',
    features: ['1,000 backlinks', '24h check interval', '10 projects', 'API access', 'Priority support'],
    priceId: 'price_pro',
    popular: true,
  },
  {
    id: 'agency',
    name: 'Agency',
    price: '$99',
    period: '/mo',
    features: ['5,000 backlinks', '12h check interval', 'Unlimited projects', 'White-label reports'],
    priceId: 'price_agency',
  },
];

function Settings() {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('profile');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Handle success/canceled from checkout
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success('Subscription upgraded successfully!');
      setActiveTab('billing');
      // Refetch billing data
      queryClient.invalidateQueries(['billing']);
      // Clean up URL
      setSearchParams({});
    } else if (searchParams.get('canceled') === 'true') {
      toast.error('Checkout was canceled');
      setActiveTab('billing');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, queryClient]);

  const { data: billingData, refetch: refetchBilling } = useQuery({
    queryKey: ['billing'],
    queryFn: fetchBilling,
  });

  const checkoutMutation = useMutation({
    mutationFn: createCheckout,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: () => {
      toast.error('Failed to create checkout session');
    },
  });

  const portalMutation = useMutation({
    mutationFn: createPortalSession,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: () => {
      toast.error('Failed to open billing portal');
    },
  });

  // Get current plan from billing data (works in both Clerk and dev mode)
  const currentPlan = billingData?.plan || user?.publicMetadata?.plan || 'free';

  // Handle different user object structures (Clerk vs Dev)
  const userEmail = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || '';
  const userName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user?.fullName || 'Not set');

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'billing', label: 'Billing', icon: CreditCard },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and preferences
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl">
        {activeTab === 'profile' && (
          <div className="glow-card-filled p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Profile Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={userEmail}
                  disabled
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-muted-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Managed by your authentication provider
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={userName}
                  disabled
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Current Plan
                </label>
                <div className="px-3 py-2 bg-muted border border-border rounded-lg text-foreground capitalize">
                  {currentPlan}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'appearance' && (
          <div className="space-y-6">
            {/* Color Mode */}
            <div className="glow-card-filled p-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">Color Mode</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Choose how BacklinkGuard looks to you. Select a theme preference.
              </p>
              <ThemeToggle />
            </div>

            {/* Accent Color */}
            <div className="glow-card-filled p-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">Accent Color</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Pick a color that reflects your style. This changes buttons, links, and highlights.
              </p>
              <AccentColorPicker />
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <NotificationsTab currentPlan={currentPlan} />
        )}

        {activeTab === 'billing' && (
          <div className="space-y-6">
            {/* Current Plan */}
            <div className="glow-card-filled p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Current Plan</h2>
                {currentPlan !== 'free' && (
                  <button
                    onClick={() => portalMutation.mutate()}
                    disabled={portalMutation.isPending}
                    className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                  >
                    Manage Billing
                    <ExternalLink className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-3xl font-bold text-foreground capitalize">
                  {currentPlan}
                </div>
                {billingData?.subscription && (
                  <span className="text-sm text-muted-foreground">
                    Next billing: {new Date(billingData.subscription.current_period_end * 1000).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            {/* Plans Grid */}
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4">Available Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative rounded-xl p-6 transition-all duration-300 ${
                      plan.popular
                        ? 'glow-card border-primary/40 shadow-[0_0_25px_-5px_hsl(var(--primary)/0.25)]'
                        : 'glow-card-filled'
                    } ${plan.id === currentPlan ? 'ring-2 ring-primary shadow-[0_0_30px_-5px_hsl(var(--primary)/0.3)]' : ''}`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-medium">
                        Popular
                      </div>
                    )}
                    <div className="text-center mb-4">
                      <h3 className="font-semibold text-foreground">{plan.name}</h3>
                      <div className="mt-2">
                        <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                        <span className="text-muted-foreground">{plan.period}</span>
                      </div>
                    </div>
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-foreground/80">
                          <Check className="h-4 w-4 text-success shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {plan.id === currentPlan ? (
                      <button
                        disabled
                        className="w-full py-2 px-4 rounded-lg bg-muted text-muted-foreground text-sm"
                      >
                        Current Plan
                      </button>
                    ) : plan.priceId ? (
                      <button
                        onClick={() => checkoutMutation.mutate({ price_id: plan.priceId })}
                        disabled={checkoutMutation.isPending}
                        className="w-full py-2 px-4 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm transition-colors disabled:opacity-50"
                      >
                        {checkoutMutation.isPending ? 'Loading...' : 'Upgrade'}
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full py-2 px-4 rounded-lg bg-muted text-muted-foreground text-sm"
                      >
                        Free
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// Notifications Tab Component
function NotificationsTab({ currentPlan }) {
  return (
    <div className="space-y-6">
      <div className="glow-card-filled p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Notification Channels</h2>
        <p className="text-muted-foreground mb-6">
          Configure how you receive alert notifications when backlink status changes.
        </p>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-card/50 border border-border/50 rounded-lg">
            <div>
              <p className="font-medium text-foreground">Email Alerts</p>
              <p className="text-sm text-muted-foreground">Receive alerts via email (Emailit)</p>
            </div>
            <span className="text-success text-sm">Available</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-card/50 border border-border/50 rounded-lg">
            <div>
              <p className="font-medium text-foreground">Slack Alerts</p>
              <p className="text-sm text-muted-foreground">Receive alerts in Slack</p>
            </div>
            <span className={currentPlan === 'free' ? 'text-muted-foreground text-sm' : 'text-success text-sm'}>
              {currentPlan === 'free' ? 'Upgrade required' : 'Available'}
            </span>
          </div>
          <div className="flex items-center justify-between p-4 bg-card/50 border border-border/50 rounded-lg">
            <div>
              <p className="font-medium text-foreground">n8n Webhook</p>
              <p className="text-sm text-muted-foreground">Custom workflow automation</p>
            </div>
            <span className="text-success text-sm">Available</span>
          </div>
        </div>
      </div>

      <div className="glow-card-filled p-6">
        <div className="flex items-center gap-2 mb-2">
          <Plug className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Configure Integrations</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          API keys and webhook URLs for notification services are managed on the Integrations page.
        </p>
        <Link
          to="/integrations"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm"
        >
          <Plug className="h-4 w-4" />
          Go to Integrations
        </Link>
      </div>
    </div>
  );
}


export default Settings;
